import apiClient from '../utils/apiClient';

/**
 * Admin broadcast API — `/api/admin/announcements/*`.
 *
 * Every method resolves to `{ success, data?, message }` and never throws, so
 * the page can render a Persian error without a try/catch at each call site.
 */

export type AnnouncementStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'cancelled'
  | 'failed';

export type AudienceType =
  | 'all'
  | 'buyers'
  | 'sellers'
  | 'admins'
  | 'specific'
  | 'segment'
  | 'rules';

/** یک شرط مخاطب قاعده‌محور — کلید از کاتالوگ بک‌اند (meta.attributes) */
export interface AudienceRule {
  key: string;
  op?: 'is' | 'gte' | 'lte' | 'eq' | 'in' | 'nin';
  value?: any;
  /** نفی کل شرط */
  not?: boolean;
}
export type Match = 'all' | 'any';
export interface AudienceGroup { match: Match; rules: AudienceRule[] }
export interface AudienceDef { match: Match; groups: AudienceGroup[] }

export interface AttrOption { value: string; label: string }
export interface AttrMeta {
  key: string;
  label: string;
  group: string;
  type: 'bool' | 'number' | 'days' | 'enum' | 'multi' | 'lookup';
  help: string;
  unit?: string;
  options?: AttrOption[];
  yes?: string;
  no?: string;
  lookup?: 'store' | 'product' | 'accountType' | 'announcement';
  ops?: Array<'gte' | 'lte' | 'eq'>;
}
export interface AudiencePreset { id: string; label: string; help: string; def: AudienceDef }
export interface Placeholder { key: string; label: string; help: string }

export interface AudienceSegment {
  _id: string;
  name: string;
  description?: string;
  rules: AudienceRule[];
  match?: Match;
  groups?: AudienceGroup[];
  def?: AudienceDef;
  describe?: string[];
  describeFull?: string;
  lastCount?: number;
  lastCountAt?: string;
  usageCount: number;
  createdBy?: { _id: string; name?: string } | string;
  createdAt: string;
  updatedAt: string;
}

export interface AudienceFilters {
  platforms?: string[];
  hasPushToken?: boolean;
  registeredWithinDays?: number;
  registeredBeforeDays?: number;
  activeWithinDays?: number;
  inactiveForDays?: number;
  minOrders?: number;
  maxOrders?: number;
  kycVerified?: boolean;
  minFigaPoints?: number;
}

export interface Audience {
  type: AudienceType;
  userIds?: string[];
  filters?: AudienceFilters;
  rules?: AudienceRule[];
  match?: Match;
  groups?: AudienceGroup[];
  /** شماره/ایمیل/شناسه — «به‌علاوه» و «به‌جز» */
  includeIds?: string[];
  excludeIds?: string[];
  segmentId?: string;
  segmentName?: string;
}

export interface AnnouncementStats {
  targeted: number;
  delivered: number;
  pushTargeted: number;
  pushSent: number;
  pushFailed: number;
  pushSuppressed: number;
  failed: number;
  smsTargeted?: number;
  smsSent?: number;
  smsFailed?: number;
  emailTargeted?: number;
  emailSent?: number;
  emailFailed?: number;
}

export interface Announcement {
  _id: string;
  title: string;
  message: string;
  notificationType: string;
  category: string;
  linkUrl?: string;
  imageUrl?: string;
  audience: Audience;
  channels: { inApp: boolean; push: boolean; sms?: boolean; email?: boolean };
  smsText?: string;
  emailSubject?: string;
  force: boolean;
  status: AnnouncementStatus;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  stats: AnnouncementStats;
  error?: string;
  createdBy?: { _id: string; name?: string; email?: string; phoneNumber?: string } | string;
  cancelledBy?: { _id: string; name?: string } | string;
  createdAt: string;
  updatedAt: string;
}

export interface PushCategoryMeta {
  id: string;
  label: string;
  description: string;
  importance: 'high' | 'default' | 'low';
  mutable: boolean;
}

export interface AnnouncementMeta {
  categories: PushCategoryMeta[];
  audiences: Array<{ id: AudienceType; label: string; description: string }>;
  platforms: string[];
  attributes?: AttrMeta[];
  presets?: AudiencePreset[];
  placeholders?: Placeholder[];
}

export interface RecipientUser {
  _id: string;
  name: string;
  phoneNumber?: string;
  email?: string;
}

export interface AudiencePreview {
  total: number;
  withPush: number;
  withPhone?: number;
  withEmail?: number;
  byPlatform: Record<string, number>;
  sample: Array<{ _id: string; name: string; phoneNumber?: string; email?: string; platforms?: string[] }>;
  describe?: string;
  /** Identifiers in a `specific` audience that matched no user. */
  notFound?: string[];
}

export interface Paginated<T> {
  docs: T[];
  totalDocs: number;
  page: number;
  totalPages: number;
  limit: number;
}

interface Result<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// `apiClient` already has `/api` in its baseURL (REACT_APP_API_URL).
const BASE = '/admin/announcements';

const fail = (error: any, fallback: string): Result<never> => ({
  success: false,
  message: error?.response?.data?.message || fallback,
});

export const announcementApi = {
  async getMeta(): Promise<Result<AnnouncementMeta>> {
    try {
      const res = await apiClient.get(`${BASE}/meta`);
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در دریافت تنظیمات اطلاعیه');
    }
  },

  async list(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  } = {}): Promise<Result<Paginated<Announcement>>> {
    try {
      const res = await apiClient.get(BASE, { params });
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در دریافت فهرست اطلاعیه‌ها');
    }
  },

  async getOne(id: string): Promise<Result<Announcement>> {
    try {
      const res = await apiClient.get(`${BASE}/${id}`);
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در دریافت اطلاعیه');
    }
  },

  /** Type-ahead lookup by mobile number, name, email or id. */
  async searchRecipients(q: string): Promise<Result<RecipientUser[]>> {
    try {
      const res = await apiClient.get(`${BASE}/recipients`, { params: { q } });
      return { success: true, data: res.data?.data ?? [] };
    } catch (error) {
      return fail(error, 'خطا در جستجوی کاربران');
    }
  },

  /** Recipient count for an audience, before anything is sent. */
  async preview(audience: Audience): Promise<Result<AudiencePreview>> {
    try {
      const res = await apiClient.post(`${BASE}/preview`, { audience });
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در محاسبهٔ مخاطبان');
    }
  },

  async create(payload: Partial<Announcement> & { scheduledAt?: string | null }): Promise<Result<Announcement>> {
    try {
      const res = await apiClient.post(BASE, payload);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ایجاد اطلاعیه');
    }
  },

  async update(
    id: string,
    payload: Partial<Announcement> & { scheduledAt?: string | null },
  ): Promise<Result<Announcement>> {
    try {
      const res = await apiClient.put(`${BASE}/${id}`, payload);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در به‌روزرسانی اطلاعیه');
    }
  },

  async send(id: string): Promise<Result<null>> {
    try {
      const res = await apiClient.post(`${BASE}/${id}/send`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ارسال اطلاعیه');
    }
  },

  /** Sends the draft to the logged-in admin only. */
  async sendTest(payload: { id?: string } & Partial<Announcement>): Promise<Result<null>> {
    try {
      const res = await apiClient.post(`${BASE}/test`, payload);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ارسال اطلاعیهٔ تستی');
    }
  },

  async cancel(id: string): Promise<Result<null>> {
    try {
      const res = await apiClient.post(`${BASE}/${id}/cancel`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در لغو اطلاعیه');
    }
  },

  async duplicate(id: string): Promise<Result<Announcement>> {
    try {
      const res = await apiClient.post(`${BASE}/${id}/duplicate`);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ساخت رونوشت');
    }
  },

  async remove(id: string): Promise<Result<null>> {
    try {
      const res = await apiClient.delete(`${BASE}/${id}`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در حذف اطلاعیه');
    }
  },

  // ── بخش‌های مخاطب ذخیره‌شده
  async listSegments(): Promise<Result<AudienceSegment[]>> {
    try {
      const res = await apiClient.get(`${BASE}/segments`);
      return { success: true, data: res.data?.data ?? [] };
    } catch (error) {
      return fail(error, 'خطا در دریافت بخش‌ها');
    }
  },
  /** جست‌وجوی مقادیر انتخابی برای شرط‌های lookup */
  async lookup(kind: string, q: string, ids?: string[]): Promise<Result<AttrOption[]>> {
    try {
      const res = await apiClient.get(`${BASE}/lookup`, { params: { kind, q, ids: ids?.join(',') || undefined } });
      return { success: true, data: res.data?.data ?? [] };
    } catch (error) {
      return fail(error, 'خطا در جست‌وجو');
    }
  },
  async createSegment(payload: { name: string; description?: string; def: AudienceDef }): Promise<Result<AudienceSegment>> {
    try {
      const res = await apiClient.post(`${BASE}/segments`, payload);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ذخیرهٔ بخش');
    }
  },
  async updateSegment(id: string, payload: { name?: string; description?: string; def?: AudienceDef }): Promise<Result<AudienceSegment>> {
    try {
      const res = await apiClient.put(`${BASE}/segments/${id}`, payload);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در به‌روزرسانی بخش');
    }
  },
  async deleteSegment(id: string): Promise<Result<null>> {
    try {
      const res = await apiClient.delete(`${BASE}/segments/${id}`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در حذف بخش');
    }
  },
  async countSegment(id: string): Promise<Result<AudiencePreview>> {
    try {
      const res = await apiClient.post(`${BASE}/segments/${id}/count`);
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در شمارش');
    }
  },
};

export default announcementApi;
